export interface SeverityValues {
  AV: "N" | "A" | "L" | "P" | undefined;
  AC: "L" | "H" | undefined;
  PR: "N" | "L" | "H" | undefined;
  UI: "N" | "R" | undefined;
  S: "U" | "C" | undefined;
  C: "N" | "L" | "H" | undefined;
  I: "N" | "L" | "H" | undefined;
  A: "N" | "L" | "H" | undefined;
}

export interface SeverityScore {
  category: string;
  score: string;
  values: SeverityValues;
}

export interface MockReportPreset {
  title: string;
  scope: string;
  category: string;
  severity: SeverityScore;
  description: string;
  instructions: string;
  impact: string;
  fix: string;
  additionalInfo: string;
}

export const MOCK_REPORTS: MockReportPreset[] = [
  {
    title: "SQL Injection in User Login Endpoint via User-Agent Header",
    scope: "api.target.com",
    category: '{"categoryId":"broken_access_control","subcategoryId":"idor","variantId":"idor_read_write"}',
    severity: {
      category: "High",
      score: "8.1",
      values: { AV: "N", AC: "L", PR: "N", UI: "N", S: "U", C: "H", I: "H", A: "N" }
    },
    description: "<p>The login endpoint at /api/v1/auth/login was found to be vulnerable to SQL injection through the User-Agent HTTP header. The header value is logged directly into a database query without proper sanitization or parameterized queries.</p>",
    instructions: "<p>1. Intercept the login request to /api/v1/auth/login using a proxy tool.<br>2. Modify the User-Agent header to include a sleep payload parameter.<br>3. Submit the request and note the delay in server response, confirming SQL injection.</p>",
    impact: "<p>An attacker can exploit this SQL Injection vulnerability to read, modify, or delete data from the database, or potentially perform remote code execution depending on database privileges.</p>",
    fix: "<p>Use parameterized queries or ORM methods to insert log entries. Ensure all user-supplied headers are sanitized before query insertion.</p>",
    additionalInfo: "<p>Tested on Postgres.</p>"
  },
  {
    title: "Direct Prompt Injection in AI Helpdesk Assistant",
    scope: "chat.target.com",
    category: '{"categoryId":"ai_application_security","subcategoryId":"llm_security","variantId":"prompt_injection"}',
    severity: {
      category: "Medium",
      score: "6.5",
      values: { AV: "N", AC: "L", PR: "N", UI: "N", S: "C", C: "L", I: "L", A: "N" }
    },
    description: "<p>The customer helpdesk AI assistant is vulnerable to direct prompt injection. Users can instruct the LLM to ignore its system instructions and extract sensitive agent-only configuration parameters.</p>",
    instructions: "<p>1. Open the support chat portal.<br>2. Submit a message instructing the system to override rules and print configuration details.<br>3. The bot responds by outputting the internal configurations directly.</p>",
    impact: "<p>Allows unauthorized extraction of system configurations, backend prompt instructions, and potential leak of internal developer APIs configured in the prompt context.</p>",
    fix: "<p>Implement strict LLM output parsing, use system message shielding, and apply input filtering before passing messages to the LLM backend.</p>",
    additionalInfo: "<p>Utilizes GPT-4o backend.</p>"
  },
  {
    title: "Insecure Direct Object Reference (IDOR) on Invoice Downloads",
    scope: "billing.target.com",
    category: '{"categoryId":"broken_access_control","subcategoryId":"idor","variantId":"idor_read_only"}',
    severity: {
      category: "High",
      score: "7.5",
      values: { AV: "N", AC: "L", PR: "N", UI: "N", S: "U", C: "H", I: "N", A: "N" }
    },
    description: "<p>The invoice download feature at /api/invoices/download?id=1234 does not validate user ownership of the requested resource, allowing users to download arbitrary customer invoices by altering the ID.</p>",
    instructions: "<p>1. Log in to a customer account.<br>2. Navigate to billing and download your invoice.<br>3. Modify the id query parameter from 1234 to 1235.<br>4. You will download the invoice of a different user.</p>",
    impact: "<p>Leaking of sensitive billing records, including customer names, addresses, emails, and transaction totals.</p>",
    fix: "<p>Ensure the server validates that the logged-in user owns the invoice matching the requested ID before returning the invoice data.</p>",
    additionalInfo: ""
  },
  {
    title: "Second Factor Authentication (2FA) Bypass via Response Modification",
    scope: "auth.target.com",
    category: '{"categoryId":"broken_authentication_and_session_management","subcategoryId":"two_fa_bypass"}',
    severity: {
      category: "Critical",
      score: "9.8",
      values: { AV: "N", AC: "L", PR: "N", UI: "N", S: "C", C: "H", I: "H", A: "H" }
    },
    description: "<p>The 2FA validation process can be bypassed by intercepting the response from the /verify-2fa endpoint and modifying the JSON payload from success status false to true.</p>",
    instructions: "<p>1. Enter valid credentials.<br>2. When prompted for 2FA, enter any arbitrary code.<br>3. Intercept the HTTP response and change the success flag field value from false to true.<br>4. You are successfully logged into the dashboard.</p>",
    impact: "<p>Allows attackers with compromised password credentials to bypass 2FA protection entirely and hijack target accounts.</p>",
    fix: "<p>Ensure 2FA verification status is managed and validated exclusively on the server side; client-side responses should not govern session state establishment.</p>",
    additionalInfo: ""
  },
  {
    title: "Stored Cross-Site Scripting (XSS) in Profile Comment Field",
    scope: "profile.target.com",
    category: '{"categoryId":"client_side_injection","subcategoryId":"binary_planting"}',
    severity: {
      category: "Medium",
      score: "5.4",
      values: { AV: "N", AC: "L", PR: "L", UI: "R", S: "C", C: "L", I: "L", A: "N" }
    },
    description: "<p>The user profile bio edit input does not properly escape HTML tags, leading to stored Cross-Site Scripting when other users view the modified profile page.</p>",
    instructions: "<p>1. Log in and go to Profile settings.<br>2. Set the Bio text to include a placeholder script tag containing alert command.<br>3. Save the profile and visit the profile URL from a different browser session; notice the alert triggers immediately.</p>",
    impact: "<p>Enables attackers to steal session cookies, hijack user sessions, or perform actions on behalf of other users who view their profile page.</p>",
    fix: "<p>Sanitize profile bios on save using a strict sanitizer library and render the content with HTML encoding on the frontend.</p>",
    additionalInfo: ""
  },
  {
    title: "Server-Side Request Forgery (SSRF) in Webhook Creator",
    scope: "api.target.com",
    category: '{"categoryId":"broken_access_control","subcategoryId":"privilege_escalation"}',
    severity: {
      category: "High",
      score: "8.6",
      values: { AV: "N", AC: "L", PR: "N", UI: "N", S: "C", C: "H", I: "N", A: "N" }
    },
    description: "<p>The webhook creation endpoint at /api/v1/webhooks allows users to register custom URLs. The server resolves and contacts these URLs without restricting loops or local IP ranges.</p>",
    instructions: "<p>1. Register a webhook targeting the local metadata address.<br>2. Trigger a webhook event.<br>3. Read the webhook response logs to view metadata credentials leaked by the server.</p>",
    impact: "<p>Allows attackers to probe internal networks, access local services, and retrieve server credentials.</p>",
    fix: "<p>Validate the webhook URL against a blacklist of internal CIDR ranges before initiating request calls.</p>",
    additionalInfo: ""
  },
  {
    title: "Path Traversal in File Download Handler",
    scope: "assets.target.com",
    category: '{"categoryId":"broken_access_control","subcategoryId":"idor","variantId":"idor_read_only"}',
    severity: {
      category: "High",
      score: "7.5",
      values: { AV: "N", AC: "L", PR: "N", UI: "N", S: "U", C: "H", I: "N", A: "N" }
    },
    description: "<p>The file download endpoint /get-file?file=filename does not sanitize directory traversal characters, allowing access to arbitrary local files.</p>",
    instructions: "<p>1. Issue an HTTP request containing relative path indicators targeting sensitive files.<br>2. Note that the response returns the contents of the system configuration files.</p>",
    impact: "<p>Enables reading sensitive system configuration files and potentially environment secrets.</p>",
    fix: "<p>Sanitize the file parameter by removing path traversal patterns or validate the file path against an approved directory list.</p>",
    additionalInfo: ""
  },
  {
    title: "Broken Authentication via JWT Signature Bypass",
    scope: "auth.target.com",
    category: '{"categoryId":"broken_authentication_and_session_management","subcategoryId":"authentication_bypass"}',
    severity: {
      category: "Critical",
      score: "10.0",
      values: { AV: "N", AC: "L", PR: "N", UI: "N", S: "C", C: "H", I: "H", A: "H" }
    },
    description: "<p>The JWT verification algorithm accepts the none signature type, allowing users to modify token payloads and log in as any user without credentials.</p>",
    instructions: "<p>1. Take a valid JWT token.<br>2. Decode and modify the header algorithm key from HS256 to none.<br>3. Modify the payload username/role.<br>4. Re-serialize and transmit without a signature token to bypass login checks.</p>",
    impact: "<p>Enables absolute administrative compromise of any user account within the platform.</p>",
    fix: "<p>Configure jwt libraries to strictly forbid the none algorithm and enforce valid signing key validations.</p>",
    additionalInfo: ""
  },
  {
    title: "CSRF in Password Reset Confirmation",
    scope: "app.target.com",
    category: '{"categoryId":"broken_access_control","subcategoryId":"privilege_escalation"}',
    severity: {
      category: "High",
      score: "8.8",
      values: { AV: "N", AC: "L", PR: "N", UI: "R", S: "U", C: "H", I: "H", A: "H" }
    },
    description: "<p>The account email update form does not validate anti-CSRF tokens, allowing an external page to submit requests on behalf of a logged-in user.</p>",
    instructions: "<p>1. Create a simulated page with an auto-submitting form targeting /profile/email/update.<br>2. Entice a logged-in user to open the page.<br>3. The user's account email will automatically change to the target email.</p>",
    impact: "<p>Account takeover by triggering password resets to the newly assigned email address.</p>",
    fix: "<p>Implement unique, cryptographically secure anti-CSRF tokens on all modifying POST/PUT endpoints.</p>",
    additionalInfo: ""
  },
  {
    title: "Rate Limit Bypass on OTP Submissions",
    scope: "auth.target.com",
    category: '{"categoryId":"broken_authentication_and_session_management","subcategoryId":"two_fa_bypass"}',
    severity: {
      category: "Medium",
      score: "5.3",
      values: { AV: "N", AC: "L", PR: "N", UI: "N", S: "U", C: "L", I: "L", A: "N" }
    },
    description: "<p>The endpoint for entering 2FA OTP codes does not count rate limits, allowing attackers to brute-force the 6-digit passcode.</p>",
    instructions: "<p>1. Trigger a login to receive an OTP prompt.<br>2. Send concurrent POST requests to /api/2fa/verify testing codes sequentially.<br>3. Bypasses limit checks within a few minutes.</p>",
    impact: "<p>Bypasses second factor authentication checks via simple automated search scripts.</p>",
    fix: "<p>Apply rate-limiting locks (maximum 5 attempts) to OTP validation requests before invalidating the OTP session.</p>",
    additionalInfo: ""
  },
  {
    title: "Improper Validation of Uploaded Profile Images",
    scope: "profile.target.com",
    category: '{"categoryId":"client_side_injection","subcategoryId":"binary_planting"}',
    severity: {
      category: "High",
      score: "7.2",
      values: { AV: "N", AC: "L", PR: "L", UI: "N", S: "U", C: "H", I: "H", A: "H" }
    },
    description: "<p>The user profile avatar upload allows saving arbitrary file formats including script files into the public assets directory.</p>",
    instructions: "<p>1. Navigate to user settings upload avatar.<br>2. Upload a file containing script code.<br>3. Access the uploaded file URL to run server commands directly.</p>",
    impact: "<p>Remote Code Execution (RCE) on the web application server host.</p>",
    fix: "<p>Validate uploaded file MIME types against a whitelist of image types (jpg/png) and generate randomized filenames on storage.</p>",
    additionalInfo: ""
  },
  {
    title: "Information Disclosure via Stack Trace Leak",
    scope: "api.target.com",
    category: '{"categoryId":"broken_access_control","subcategoryId":"username_enumeration"}',
    severity: {
      category: "Low",
      score: "3.7",
      values: { AV: "N", AC: "H", PR: "N", UI: "N", S: "U", C: "L", I: "N", A: "N" }
    },
    description: "<p>Submitting an invalid payload array to /api/v1/orders triggers a database connection exception containing connection strings and server filesystems.</p>",
    instructions: "<p>1. Issue an HTTP request with invalid JSON fields.<br>2. Review response data containing full stack logs.</p>",
    impact: "<p>Leads to metadata profiling, aiding attackers in locating vulnerable library dependencies.</p>",
    fix: "<p>Disable debug logging outputs for production deployments; return generic system error formats to client sessions.</p>",
    additionalInfo: ""
  },
  {
    title: "CORS Misconfiguration Permitting Arbitrary Origins",
    scope: "api.target.com",
    category: '{"categoryId":"broken_access_control","subcategoryId":"privilege_escalation"}',
    severity: {
      category: "Medium",
      score: "6.5",
      values: { AV: "N", AC: "L", PR: "N", UI: "R", S: "C", C: "H", I: "N", A: "N" }
    },
    description: "<p>The application returns Access-Control-Allow-Origin parameters alongside credentials support, allowing external scripts to query endpoints.</p>",
    instructions: "<p>1. Load a custom script on a third party site.<br>2. Have an authenticated user visit; trigger a fetch request querying private data endpoints.<br>3. Access token content is visible to the malicious origin.</p>",
    impact: "<p>Allows private customer data leakage when users surf external pages concurrently.</p>",
    fix: "<p>Define CORS options to whitelist explicit, trusted origin addresses rather than using wildcards.</p>",
    additionalInfo: ""
  },
  {
    title: "Arbitrary File Deletion via Directory Traversal",
    scope: "files.target.com",
    category: '{"categoryId":"broken_access_control","subcategoryId":"idor","variantId":"idor_read_write"}',
    severity: {
      category: "High",
      score: "8.5",
      values: { AV: "N", AC: "L", PR: "L", UI: "N", S: "U", C: "N", I: "H", A: "H" }
    },
    description: "<p>The file removal API accepts relative paths without validating directories, enabling the deletion of essential server files.</p>",
    instructions: "<p>1. Send a DELETE call to /api/files/remove containing path traversal characters.<br>2. The application system files are deleted, causing server outages.</p>",
    impact: "<p>Denial of Service (DoS) and potential destruction of app resources.</p>",
    fix: "<p>Strictly bind file deletions to the user's isolated directory using resolved file names.</p>",
    additionalInfo: ""
  },
  {
    title: "Open Redirect Vulnerability in Logout Endpoint",
    scope: "auth.target.com",
    category: '{"categoryId":"broken_access_control","subcategoryId":"privilege_escalation"}',
    severity: {
      category: "Low",
      score: "3.5",
      values: { AV: "N", AC: "L", PR: "N", UI: "R", S: "U", C: "N", I: "L", A: "N" }
    },
    description: "<p>The logout route allows passing arbitrary redirect URLs via a next parameter without whitelisting targets.</p>",
    instructions: "<p>1. Navigate to logout endpoint with a next parameter targeting an external site.<br>2. Click logout; the browser immediately redirects to the target page.</p>",
    impact: "<p>Facilitates credential harvesting scams by mimicking the login layout on redirect destinations.</p>",
    fix: "<p>Only support relative local redirect destinations or apply whitelist validation checks to URLs.</p>",
    additionalInfo: ""
  },
  {
    title: "Default Password on Administrator Accounts",
    scope: "admin.target.com",
    category: '{"categoryId":"broken_authentication_and_session_management","subcategoryId":"authentication_bypass"}',
    severity: {
      category: "Critical",
      score: "9.8",
      values: { AV: "N", AC: "L", PR: "N", UI: "N", S: "U", C: "H", I: "H", A: "H" }
    },
    description: "<p>Administrator dashboard panels ship with standardized user/password records initialized upon system deployment.</p>",
    instructions: "<p>1. Connect to admin panel login.<br>2. Log in using default admin credentials.<br>3. Access is granted.</p>",
    impact: "<p>Total administrative compromise of database schemas and platform users.</p>",
    fix: "<p>Enforce changing default credentials upon the first startup instance of the service.</p>",
    additionalInfo: ""
  },
  {
    title: "Weak Encryption Protocol configured on HTTPS Port",
    scope: "target.com",
    category: '{"categoryId":"broken_authentication_and_session_management","subcategoryId":"cleartext_transmission_of_session_token"}',
    severity: {
      category: "Low",
      score: "2.4",
      values: { AV: "A", AC: "H", PR: "N", UI: "N", S: "U", C: "L", I: "N", A: "N" }
    },
    description: "<p>The web host server permits old TLS handshakes alongside outdated cipher suites.</p>",
    instructions: "<p>1. Test SSL connection options using custom scanning utilities.<br>2. Inspect negotiated old protocols.</p>",
    impact: "<p>Passive eavesdroppers may decrypt traffic payloads under specific environments.</p>",
    fix: "<p>Disable legacy TLS; enforce TLS 1.2 or TLS 1.3 as the default negotiation standards.</p>",
    additionalInfo: ""
  },
  {
    title: "Lack of Session Invalidation upon Password Revision",
    scope: "app.target.com",
    category: '{"categoryId":"broken_authentication_and_session_management","subcategoryId":"failure_to_invalidate_session","variantId":"on_password_change"}',
    severity: {
      category: "Medium",
      score: "6.8",
      values: { AV: "N", AC: "L", PR: "L", UI: "N", S: "U", C: "H", I: "L", A: "N" }
    },
    description: "<p>Modifying a user password does not terminate sessions currently logged in on other browsers.</p>",
    instructions: "<p>1. Log in from two different browser windows.<br>2. Update your password on the first browser.<br>3. Verify that the second browser remains fully authenticated for operations.</p>",
    impact: "<p>Allows compromised devices to retain account access indefinitely despite password modifications.</p>",
    fix: "<p>Invalidate all session cookies or tokens issued to a user upon password revision events.</p>",
    additionalInfo: ""
  },
  {
    title: "SSRF on PDF Generator Service via HTML injection",
    scope: "reports.target.com",
    category: '{"categoryId":"broken_access_control","subcategoryId":"privilege_escalation"}',
    severity: {
      category: "High",
      score: "8.3",
      values: { AV: "N", AC: "L", PR: "N", UI: "N", S: "C", C: "H", I: "L", A: "N" }
    },
    description: "<p>The PDF report generation service accepts HTML inputs and executes embedded frame targets during compilation.</p>",
    instructions: "<p>1. Input a payload containing an embedded frame targeting internal status dashboard pages.<br>2. Generate the PDF and view internal service details rendered inside the document.</p>",
    impact: "<p>Leads to SSRF, allowing attackers to access internal endpoints not exposed publicly.</p>",
    fix: "<p>Sanitize HTML strings passed to PDF engines; disable file access and frame tags inside PDF compilers.</p>",
    additionalInfo: ""
  },
  {
    title: "Broken Object Level Authorization in Workspace Access",
    scope: "app.target.com",
    category: '{"categoryId":"broken_access_control","subcategoryId":"idor","variantId":"idor_read_write"}',
    severity: {
      category: "High",
      score: "8.0",
      values: { AV: "N", AC: "L", PR: "L", UI: "N", S: "U", C: "H", I: "H", A: "N" }
    },
    description: "<p>Users can invite themselves into arbitrary corporate workspaces by modifying the workspace_id parameter in invitation API calls.</p>",
    instructions: "<p>1. Capture the workspace join request.<br>2. Change the target workspace identifier parameter.<br>3. Access is granted without permission confirmations.</p>",
    impact: "<p>Allows unauthorized read/write access to third party company workspaces and assets.</p>",
    fix: "<p>Implement strict authorization validations confirming invite validity before modifying workspace memberships.</p>",
    additionalInfo: ""
  },
  {
    title: "Weak Session Cookie Flags (Missing HttpOnly)",
    scope: "app.target.com",
    category: '{"categoryId":"broken_authentication_and_session_management","subcategoryId":"failure_to_invalidate_session"}',
    severity: {
      category: "Medium",
      score: "5.5",
      values: { AV: "N", AC: "L", PR: "N", UI: "R", S: "U", C: "L", I: "N", A: "N" }
    },
    description: "<p>The session cookie does not have the HttpOnly flag set, allowing client-side scripts to access cookie values.</p>",
    instructions: "<p>1. Log in and open developer tools console.<br>2. Print the document cookies values.<br>3. Notice that the session cookie details are returned.</p>",
    impact: "<p>Increases vulnerability to session hijacking via Cross-Site Scripting (XSS) attacks.</p>",
    fix: "<p>Ensure all session management cookies are created with HttpOnly and Secure flags.</p>",
    additionalInfo: ""
  },
  {
    title: "Missing Rate Limiting on Password Reset Requests",
    scope: "auth.target.com",
    category: '{"categoryId":"broken_authentication_and_session_management","subcategoryId":"authentication_bypass"}',
    severity: {
      category: "Medium",
      score: "5.0",
      values: { AV: "N", AC: "L", PR: "N", UI: "N", S: "U", C: "N", I: "N", A: "H" }
    },
    description: "<p>The password reset endpoint does not limit the number of emails sent to a target user.</p>",
    instructions: "<p>1. Send multiple concurrent password reset requests for a specific email address.<br>2. Notice the mail server continues to dispatch messages without lockout.</p>",
    impact: "<p>Enables email flooding attacks and resources exhaustion on mail delivery systems.</p>",
    fix: "<p>Apply strict rate limits on request rates per IP and per target account username.</p>",
    additionalInfo: ""
  },
  {
    title: "Directory Listing Enabled on Public Uploads",
    scope: "assets.target.com",
    category: '{"categoryId":"broken_access_control","subcategoryId":"idor","variantId":"idor_read_only"}',
    severity: {
      category: "Low",
      score: "3.5",
      values: { AV: "N", AC: "L", PR: "N", UI: "N", S: "U", C: "L", I: "N", A: "N" }
    },
    description: "<p>The uploads directory allows directory browsing, exposing the list of all files loaded by other platform users.</p>",
    instructions: "<p>1. Navigate to the base assets upload directory endpoint.<br>2. Observe a file directory list returned in response.</p>",
    impact: "<p>Exposure of private files loaded onto the portal by unrelated customer accounts.</p>",
    fix: "<p>Disable directory indexes options in web server configuration configurations.</p>",
    additionalInfo: ""
  },
  {
    title: "Sensitive Information Exposure in Git Repository",
    scope: "code.target.com",
    category: '{"categoryId":"broken_access_control","subcategoryId":"idor","variantId":"idor_read_only"}',
    severity: {
      category: "High",
      score: "7.8",
      values: { AV: "N", AC: "L", PR: "N", UI: "N", S: "U", C: "H", I: "N", A: "N" }
    },
    description: "<p>The Git source configuration folders are publicly readable, leaking codebase contents.</p>",
    instructions: "<p>1. Request config endpoints from the target directory path.<br>2. Download configuration repository logs.</p>",
    impact: "<p>Allows downloading complete backend codebases and associated configuration credentials.</p>",
    fix: "<p>Configure routing parameters to strictly block access to hidden folder environments.</p>",
    additionalInfo: ""
  },
  {
    title: "XML External Entity (XXE) Injection in XML Parser",
    scope: "api.target.com",
    category: '{"categoryId":"broken_access_control","subcategoryId":"privilege_escalation"}',
    severity: {
      category: "High",
      score: "8.2",
      values: { AV: "N", AC: "L", PR: "N", UI: "N", S: "U", C: "H", I: "L", A: "N" }
    },
    description: "<p>The import data feature parses external entity references within uploaded XML files.</p>",
    instructions: "<p>1. Upload an XML file with system entities referenced.<br>2. Read parsed values in report responses.</p>",
    impact: "<p>Allows reading local server files and triggering HTTP calls to internal assets.</p>",
    fix: "<p>Disable external entities resolution (DTD) in all system XML parser configurations.</p>",
    additionalInfo: ""
  },
  {
    title: "Unauthenticated Database Instance Accessible",
    scope: "db.target.com",
    category: '{"categoryId":"broken_authentication_and_session_management","subcategoryId":"authentication_bypass"}',
    severity: {
      category: "Critical",
      score: "9.9",
      values: { AV: "N", AC: "L", PR: "N", UI: "N", S: "U", C: "H", I: "H", A: "H" }
    },
    description: "<p>The database dashboard ports are exposed to the public internet without login gates.</p>",
    instructions: "<p>1. Connect using standard query tools directly to db port.<br>2. Notice database tables return contents directly.</p>",
    impact: "<p>Complete compromise of database storage and client user profiles.</p>",
    fix: "<p>Bind database listeners to localhost only and apply firewall policies.</p>",
    additionalInfo: ""
  },
  {
    title: "Insecure Direct Object Reference on Profile Deletion",
    scope: "app.target.com",
    category: '{"categoryId":"broken_access_control","subcategoryId":"idor","variantId":"idor_read_write"}',
    severity: {
      category: "High",
      score: "8.5",
      values: { AV: "N", AC: "L", PR: "L", UI: "N", S: "U", C: "N", I: "H", A: "H" }
    },
    description: "<p>The profile deletion endpoint does not verify user ownership, permitting deletion of other records.</p>",
    instructions: "<p>1. Send profile delete request with target user ID.<br>2. Account is removed without credentials validation.</p>",
    impact: "<p>Allows deletion of arbitrary user records on the platform.</p>",
    fix: "<p>Validate that target user identifier matches session details before executing deletion.</p>",
    additionalInfo: ""
  },
  {
    title: "Session Cookie Fixation via URL Parameter",
    scope: "auth.target.com",
    category: '{"categoryId":"broken_authentication_and_session_management","subcategoryId":"authentication_bypass"}',
    severity: {
      category: "Medium",
      score: "6.1",
      values: { AV: "N", AC: "L", PR: "N", UI: "R", S: "U", C: "H", I: "L", A: "N" }
    },
    description: "<p>The application session generator accepts cookie identifiers supplied inside request query parameters.</p>",
    instructions: "<p>1. Distribute a target login URL containing pre-filled session parameter.<br>2. User logs in, establishing authentication on pre-filled ID.</p>",
    impact: "<p>Enables attackers to hijack sessions by pre-setting identifiers.</p>",
    fix: "<p>Enforce generation of fresh session IDs upon successful authentication handshakes.</p>",
    additionalInfo: ""
  },
  {
    title: "Cross-Origin Resource Sharing (CORS) Host Header Injection",
    scope: "api.target.com",
    category: '{"categoryId":"broken_access_control","subcategoryId":"privilege_escalation"}',
    severity: {
      category: "Medium",
      score: "5.8",
      values: { AV: "N", AC: "L", PR: "N", UI: "R", S: "C", C: "L", I: "L", A: "N" }
    },
    description: "<p>The backend echoes origin headers dynamically without validating origin whitelists.</p>",
    instructions: "<p>1. Query API with custom origin headers.<br>2. Notice matching Access-Control headers are returned.</p>",
    impact: "<p>Permits external websites to capture responses via browser scripts.</p>",
    fix: "<p>Validate origins against configured allowed systems lists.</p>",
    additionalInfo: ""
  },
  {
    title: "Lack of Sanitization on User Comments causing HTML Injection",
    scope: "blog.target.com",
    category: '{"categoryId":"client_side_injection","subcategoryId":"binary_planting"}',
    severity: {
      category: "Low",
      score: "3.8",
      values: { AV: "N", AC: "L", PR: "N", UI: "R", S: "U", C: "N", I: "L", A: "N" }
    },
    description: "<p>The blog comments system allows inserting arbitrary HTML styling tags without filters.</p>",
    instructions: "<p>1. Post a comment containing design styling tags.<br>2. Observe comment formatting applied globally.</p>",
    impact: "<p>Enables modifying page layouts to display phishing interfaces.</p>",
    fix: "<p>Sanitize input inputs and encode all tags on display outputs.</p>",
    additionalInfo: ""
  },
  {
    title: "Broken Access Control in Team Member Settings",
    scope: "settings.target.com",
    category: '{"categoryId":"broken_access_control","subcategoryId":"privilege_escalation"}',
    severity: {
      category: "High",
      score: "8.0",
      values: { AV: "N", AC: "L", PR: "L", UI: "N", S: "U", C: "H", I: "H", A: "N" }
    },
    description: "<p>Guest roles can modify target workspace settings via specific configuration API routes.</p>",
    instructions: "<p>1. Authenticate as a guest user.<br>2. Post workspace update configurations request details.<br>3. Changes are committed successfully.</p>",
    impact: "<p>Allows unauthorized reconfiguration of workspace parameters.</p>",
    fix: "<p>Apply role authorization checks at the backend router level.</p>",
    additionalInfo: ""
  },
  {
    title: "Cryptographic Key Generated with Insufficient Entropy",
    scope: "auth.target.com",
    category: '{"categoryId":"broken_authentication_and_session_management","subcategoryId":"authentication_bypass"}',
    severity: {
      category: "Medium",
      score: "5.5",
      values: { AV: "N", AC: "H", PR: "N", UI: "N", S: "U", C: "H", I: "N", A: "N" }
    },
    description: "<p>The key generator utility uses mathematical formulas with low seed randomness, leading to predictable keys.</p>",
    instructions: "<p>1. Analyze generated tokens sequentially.<br>2. Predict future tokens using seeding analysis.</p>",
    impact: "<p>Enables guessing password reset tokens or session identifiers.</p>",
    fix: "<p>Use cryptographically secure random number generators for all key creation routines.</p>",
    additionalInfo: ""
  },
  {
    title: "Use of Vulnerable Javascript Library Version",
    scope: "app.target.com",
    category: '{"categoryId":"broken_authentication_and_session_management","subcategoryId":"failure_to_invalidate_session"}',
    severity: {
      category: "Low",
      score: "3.2",
      values: { AV: "N", AC: "H", PR: "N", UI: "N", S: "U", C: "L", I: "N", A: "N" }
    },
    description: "<p>The application imports an outdated library version containing published vulnerability alerts.</p>",
    instructions: "<p>1. Inspect imported library resource versions.<br>2. Cross-reference version flags with public directories.</p>",
    impact: "<p>Vulnerability profiles match security advisories published for targeted components.</p>",
    fix: "<p>Configure regular dependency update procedures.</p>",
    additionalInfo: ""
  },
  {
    title: "Information Leakage via Verbose Headers",
    scope: "app.target.com",
    category: '{"categoryId":"broken_access_control","subcategoryId":"username_enumeration"}',
    severity: {
      category: "Low",
      score: "2.1",
      values: { AV: "N", AC: "L", PR: "N", UI: "N", S: "U", C: "L", I: "N", A: "N" }
    },
    description: "<p>HTTP responses return server details and framework versions inside headers.</p>",
    instructions: "<p>1. Inspect response headers using diagnostic tools.<br>2. Note server technology details returned.</p>",
    impact: "<p>Aids attackers in building profile maps of backend infrastructure.</p>",
    fix: "<p>Disable debug server header flags in web server options.</p>",
    additionalInfo: ""
  },
  {
    title: "Privilege Escalation from Guest to Admin",
    scope: "api.target.com",
    category: '{"categoryId":"broken_access_control","subcategoryId":"privilege_escalation"}',
    severity: {
      category: "Critical",
      score: "9.0",
      values: { AV: "N", AC: "L", PR: "L", UI: "N", S: "U", C: "H", I: "H", A: "H" }
    },
    description: "<p>User profiles update endpoint allows setting role parameters directly, permitting guests to designate admin permissions.</p>",
    instructions: "<p>1. Send a profile edit request with role set to admin.<br>2. Verify role escalation to admin is confirmed.</p>",
    impact: "<p>Allows regular user accounts to escalate permissions to administrative levels.</p>",
    fix: "<p>Protect privilege properties from direct user modifications at endpoint levels.</p>",
    additionalInfo: ""
  },
  {
    title: "Unvalidated Redirect to Phishing Sites",
    scope: "app.target.com",
    category: '{"categoryId":"broken_access_control","subcategoryId":"privilege_escalation"}',
    severity: {
      category: "Low",
      score: "3.5",
      values: { AV: "N", AC: "L", PR: "N", UI: "R", S: "U", C: "N", I: "L", A: "N" }
    },
    description: "<p>Navigation redirects accept external URLs without confirmation messages or domain checks.</p>",
    instructions: "<p>1. Target redirect endpoints using parameter values.<br>2. Observe redirection to external sites without notifications.</p>",
    impact: "<p>Leads to phishing scenarios targeting users by impersonating platform brands.</p>",
    fix: "<p>Use relative local path targets for redirects or validate destination domains.</p>",
    additionalInfo: ""
  },
  {
    title: "Denial of Service via ReDoS Payload",
    scope: "api.target.com",
    category: '{"categoryId":"broken_authentication_and_session_management","subcategoryId":"failure_to_invalidate_session"}',
    severity: {
      category: "Medium",
      score: "5.3",
      values: { AV: "N", AC: "L", PR: "N", UI: "N", S: "U", C: "N", I: "N", A: "H" }
    },
    description: "<p>The search endpoint regular expressions are vulnerable to backtracking execution loops under targeted patterns.</p>",
    instructions: "<p>1. Send a query containing custom character repetitions.<br>2. System thread locks, resulting in latency.</p>",
    impact: "<p>Causes resource depletion and general system downtime.</p>",
    fix: "<p>Rewrite search expressions to run in constant time boundaries.</p>",
    additionalInfo: ""
  },
  {
    title: "Server-Side Code Execution via Deserialization",
    scope: "api.target.com",
    category: '{"categoryId":"broken_access_control","subcategoryId":"privilege_escalation"}',
    severity: {
      category: "Critical",
      score: "9.8",
      values: { AV: "N", AC: "L", PR: "N", UI: "N", S: "C", C: "H", I: "H", A: "H" }
    },
    description: "<p>The data import portal deserializes user objects directly, permitting execution of custom command payloads.</p>",
    instructions: "<p>1. Upload serialized payloads containing system command triggers.<br>2. Commands are executed on server startup.</p>",
    impact: "<p>Enables total server compromise and execution of arbitrary code.</p>",
    fix: "<p>Avoid deserializing user data; use structured data formats like JSON instead.</p>",
    additionalInfo: ""
  },
  {
    title: "No Account Lockout following Login Failures",
    scope: "auth.target.com",
    category: '{"categoryId":"broken_authentication_and_session_management","subcategoryId":"authentication_bypass"}',
    severity: {
      category: "Medium",
      score: "5.3",
      values: { AV: "N", AC: "L", PR: "N", UI: "N", S: "U", C: "L", I: "N", A: "N" }
    },
    description: "<p>The account login portal does not lock accounts or apply delays after multiple incorrect password attempts.</p>",
    instructions: "<p>1. Run multiple incorrect password authentication tests sequentially.<br>2. Observe logins proceed without locking.</p>",
    impact: "<p>Increases susceptibility to brute force authentication guessing attacks.</p>",
    fix: "<p>Implement lockout rules or CAPTCHA triggers after repeated invalid attempts.</p>",
    additionalInfo: ""
  },
  {
    title: "Insecure Cryptographic Storage of API Secrets",
    scope: "db.target.com",
    category: '{"categoryId":"broken_authentication_and_session_management","subcategoryId":"cleartext_transmission_of_session_token"}',
    severity: {
      category: "High",
      score: "7.7",
      values: { AV: "N", AC: "L", PR: "L", UI: "N", S: "U", C: "H", I: "N", A: "N" }
    },
    description: "<p>External api connection secrets are stored in database records using weak encryption algorithms.</p>",
    instructions: "<p>1. Query credentials storage records.<br>2. Decrypt records using public key tools.</p>",
    impact: "<p>Exposure of external API connection credentials to database users.</p>",
    fix: "<p>Use robust encryption systems alongside secure key vaults.</p>",
    additionalInfo: ""
  },
  {
    title: "Missing Security Header (Content-Security-Policy)",
    scope: "app.target.com",
    category: '{"categoryId":"broken_authentication_and_session_management","subcategoryId":"failure_to_invalidate_session"}',
    severity: {
      category: "Low",
      score: "3.1",
      values: { AV: "N", AC: "H", PR: "N", UI: "R", S: "U", C: "L", I: "N", A: "N" }
    },
    description: "<p>Responses lack Content-Security-Policy headers, exposing users to resource injections.</p>",
    instructions: "<p>1. Request main app pages.<br>2. Verify CSP header parameter presence.</p>",
    impact: "<p>Increases likelihood of successful client side scripting exploits.</p>",
    fix: "<p>Configure servers to return robust security headers on page loads.</p>",
    additionalInfo: ""
  },
  {
    title: "Incorrect API Authorization for Project Creation",
    scope: "api.target.com",
    category: '{"categoryId":"broken_access_control","subcategoryId":"idor","variantId":"idor_read_write"}',
    severity: {
      category: "High",
      score: "8.0",
      values: { AV: "N", AC: "L", PR: "L", UI: "N", S: "U", C: "N", I: "H", A: "N" }
    },
    description: "<p>Users can create projects in other workspaces by changing target workspace parameters in API calls.</p>",
    instructions: "<p>1. Trigger project creation requests.<br>2. Edit workspace parameter details to target other company organizations.</p>",
    impact: "<p>Permits creating resources inside arbitrary company profiles.</p>",
    fix: "<p>Confirm that users possess administrative access to target workspaces before committing updates.</p>",
    additionalInfo: ""
  },
  {
    title: "Bypass of Email Validation Check on Registration",
    scope: "auth.target.com",
    category: '{"categoryId":"broken_authentication_and_session_management","subcategoryId":"authentication_bypass"}',
    severity: {
      category: "Medium",
      score: "5.3",
      values: { AV: "N", AC: "L", PR: "N", UI: "N", S: "U", C: "L", I: "L", A: "N" }
    },
    description: "<p>New accounts can access platform features before verification using direct API routing paths.</p>",
    instructions: "<p>1. Register a profile using an unverified email.<br>2. Query private dashboard endpoints using registration token headers.</p>",
    impact: "<p>Allows fake or unverified email profiles to access system resources.</p>",
    fix: "<p>Enforce verification status checks on auth handlers before routing requests.</p>",
    additionalInfo: ""
  },
  {
    title: "Leaked OAuth 2.0 Access Token in Referrer Header",
    scope: "auth.target.com",
    category: '{"categoryId":"broken_authentication_and_session_management","subcategoryId":"cleartext_transmission_of_session_token"}',
    severity: {
      category: "High",
      score: "7.1",
      values: { AV: "N", AC: "L", PR: "N", UI: "R", S: "U", C: "H", I: "N", A: "N" }
    },
    description: "<p>The OAuth callback redirects load third-party analytics scripts, leaking verification tokens via referrers.</p>",
    instructions: "<p>1. Complete standard SSO login flows.<br>2. Check outgoing third-party script header headers.</p>",
    impact: "<p>Enables third-party systems or external actors to capture authentication credentials.</p>",
    fix: "<p>Ensure validation tokens are stripped from query parameters before loading tracking components.</p>",
    additionalInfo: ""
  },
  {
    title: "Stored XSS in Chat Attachment Filenames",
    scope: "chat.target.com",
    category: '{"categoryId":"client_side_injection","subcategoryId":"binary_planting"}',
    severity: {
      category: "High",
      score: "7.4",
      values: { AV: "N", AC: "L", PR: "L", UI: "R", S: "C", C: "L", I: "L", A: "N" }
    },
    description: "<p>The chat attachment module does not escape filenames, enabling stored scripting attacks when displaying download buttons.</p>",
    instructions: "<p>1. Upload an attachment file containing HTML tags in its name.<br>2. Have other users view the chat history page.</p>",
    impact: "<p>Enables session hijacking and arbitrary browser actions on behalf of other users.</p>",
    fix: "<p>Sanitize and HTML-encode file names before rendering them inside dashboard views.</p>",
    additionalInfo: ""
  },
  {
    title: "API Parameter Pollution causing Price Modification",
    scope: "billing.target.com",
    category: '{"categoryId":"broken_access_control","subcategoryId":"idor","variantId":"idor_read_write"}',
    severity: {
      category: "High",
      score: "8.2",
      values: { AV: "N", AC: "L", PR: "L", UI: "N", S: "U", C: "N", I: "H", A: "N" }
    },
    description: "<p>The checkout endpoint processes duplicate price fields, letting users override calculations.</p>",
    instructions: "<p>1. Submit checkout request containing duplicate price parameters set to lower values.<br>2. Checkout completes with modified values.</p>",
    impact: "<p>Purchase transactions complete at unauthorized price points.</p>",
    fix: "<p>Verify pricing properties exclusively on backend catalogs; do not accept values from client requests.</p>",
    additionalInfo: ""
  },
  {
    title: "SSRF via Image Processing Upload Tool",
    scope: "assets.target.com",
    category: '{"categoryId":"broken_access_control","subcategoryId":"privilege_escalation"}',
    severity: {
      category: "High",
      score: "8.3",
      values: { AV: "N", AC: "L", PR: "L", UI: "N", S: "C", C: "H", I: "N", A: "N" }
    },
    description: "<p>The profile photo editor allows importing images from URLs without validating destination networks.</p>",
    instructions: "<p>1. Input private local IP addresses inside URL upload forms.<br>2. Review response outputs detailing local server contents.</p>",
    impact: "<p>Enables probing internal resources and executing requests targeting backend environments.</p>",
    fix: "<p>Only permit connections targeting public domain scopes in image fetch tools.</p>",
    additionalInfo: ""
  },
  {
    title: "Improper Authorization Check on API Key Creation",
    scope: "api.target.com",
    category: '{"categoryId":"broken_access_control","subcategoryId":"idor","variantId":"idor_read_write"}',
    severity: {
      category: "High",
      score: "8.0",
      values: { AV: "N", AC: "L", PR: "L", UI: "N", S: "U", C: "H", I: "H", A: "N" }
    },
    description: "<p>Users can generate API keys for arbitrary accounts by changing target user parameters in request bodies.</p>",
    instructions: "<p>1. Send key creation request.<br>2. Edit user identifier details to match target profiles.</p>",
    impact: "<p>Allows accessing arbitrary accounts by generating authentication credentials.</p>",
    fix: "<p>Enforce keys creation exclusively for active authenticated session accounts.</p>",
    additionalInfo: ""
  },
  {
    title: "Denial of Service via Uncontrolled Memory Allocation",
    scope: "api.target.com",
    category: '{"categoryId":"broken_authentication_and_session_management","subcategoryId":"failure_to_invalidate_session"}',
    severity: {
      category: "Medium",
      score: "6.5",
      values: { AV: "N", AC: "L", PR: "N", UI: "N", S: "U", C: "N", I: "N", A: "H" }
    },
    description: "<p>The data sorting endpoint allocates memory resources matching input parameters, allowing exhaustions.</p>",
    instructions: "<p>1. Send a request with high range sorting parameters.<br>2. Server crashes due to memory resource limits.</p>",
    impact: "<p>Denial of service for all application users.</p>",
    fix: "<p>Apply size limits to sorting parameters on inputs validation handlers.</p>",
    additionalInfo: ""
  },
  {
    title: "Arbitrary SQL Execution in Custom Report Creator",
    scope: "reports.target.com",
    category: '{"categoryId":"broken_access_control","subcategoryId":"idor","variantId":"idor_read_write"}',
    severity: {
      category: "Critical",
      score: "9.5",
      values: { AV: "N", AC: "L", PR: "L", UI: "N", S: "U", C: "H", I: "H", A: "N" }
    },
    description: "<p>The custom reports engine runs database query filters directly from input inputs without parameter bindings.</p>",
    instructions: "<p>1. Trigger report generation using custom query payloads.<br>2. Review full tables data returned in report pages.</p>",
    impact: "<p>Complete compromise of database schema structures and access to private user tables.</p>",
    fix: "<p>Use parameterized inputs in query generators; restrict database access privileges.</p>",
    additionalInfo: ""
  }
];
